import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CloudOff, Loader2, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { flushQueue, getQueue, removeFromQueue } from '../lib/offlineQueue';

/**
 * Compact card showing the list of offline-queued entries.
 * Drop this anywhere; it hides itself when the queue is empty.
 */
export default function OfflineQueuePanel({ title = 'Pending Offline Entries' }) {
  const [items, setItems] = useState(getQueue());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handler = () => setItems(getQueue());
    window.addEventListener('offline-queue-changed', handler);
    return () => window.removeEventListener('offline-queue-changed', handler);
  }, []);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await flushQueue(api);
    } finally {
      setSyncing(false);
      setItems(getQueue());
    }
  };

  const handleDiscard = (id) => {
    removeFromQueue(id);
    setItems(getQueue());
  };

  if (items.length === 0) return null;

  // Label + icon per entry type
  const typeConfig = {
    form_submission: { label: 'Form submission', color: 'text-[#0E7490]' },
    task_create: { label: 'New task', color: 'text-[#1A56DB]' },
    task_update: { label: 'Task update', color: 'text-[#1A56DB]' },
    note_create: { label: 'Clinical note', color: 'text-[#7C3AED]' },
    clinical_note_soap: { label: 'SOAP note', color: 'text-[#7C3AED]' },
    appointment_create: { label: 'New appointment', color: 'text-[#CA8A04]' },
    write: { label: 'Pending change', color: 'text-[#64748B]' },
  };
  const fmtTypeLabel = (t) => typeConfig[t]?.label || (t || 'Pending change').replace(/_/g, ' ');
  const fmtTypeColor = (t) => typeConfig[t]?.color || 'text-[#64748B]';

  return (
    <Card className="careiq-card border-l-4 border-l-[#CA8A04]" data-testid="offline-queue-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold text-[#0A2342] flex items-center gap-2">
          <CloudOff className="w-4 h-4 text-[#CA8A04]" />
          {title}
          <span className="text-xs font-normal text-[#64748B]">({items.length})</span>
        </CardTitle>
        <Button size="sm" className="careiq-btn-primary" onClick={handleSyncAll} disabled={syncing} data-testid="offline-sync-all-btn">
          {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Sync all
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-2 rounded border border-[#E2E8F0] bg-white" data-testid={`offline-item-${item.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0A2342] truncate">
                  <span className={`text-[10px] font-semibold uppercase mr-2 ${fmtTypeColor(item.type)}`}>{fmtTypeLabel(item.type)}</span>
                  {item.meta?.formName || item.meta?.title || item.meta?.subject || item.meta?.scheduledDate || ''}
                </div>
                <div className="text-[10px] text-[#64748B] truncate">
                  Saved {new Date(item.createdAt).toLocaleString()}
                  {item.retryCount > 0 && <span className="text-[#991B1B] ml-2">· {item.retryCount} retry{item.retryCount > 1 ? 's' : ''}</span>}
                </div>
                {item.lastError && (
                  <div className="text-[10px] text-[#991B1B] truncate flex items-center gap-1 mt-0.5">
                    <AlertTriangle className="w-2.5 h-2.5" />{item.lastError}
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDiscard(item.id)} title="Discard" data-testid={`offline-discard-${item.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-[#991B1B]" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
